import Button from '@/components/basicComponents/button';
import { themeVar } from '@/styles/theme';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import {
  DealRegistryPageListDueDiligenceRecordsQuery,
  DueDiligenceFunnelListStatusFilter,
  DueDiligenceFunnelSortBy,
  DueDiligenceFunnelSortDirection,
  DueDiligencePageGetDueDiligenceFunnelQuery,
  DueDiligencePageListDueDiligenceFunnelsQuery,
  DueDiligenceRecordsListSortBy,
  DueDiligenceRecordsSortBy,
  DueDiligenceRecordsSortDirection,
  DueDiligenceRecordStatus,
  Security_Status
} from '@/__generated__/graphql';
import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import { BurgerMenuIcon } from '@/assets/static/icons/burger-menu';
import { CloseMenuIcon } from '@/assets/static/icons/close-menu';
import { ISort } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { useClickOutside } from '@/hooks/useClickOutside';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { TabsTitle } from '../basicComponents/tabs';
import { LIST_DUE_DILIGENCE_RECORD_QUERY } from '../dealRegistry-page/queries';
import Header from '../header';
import { Loader } from '../loaders/loader';
import { LoaderOnWholeScreen } from '../loaders/loader-on-whole-screen';
import { dueDiligenceFunnelSort } from './constants';
import EditOwner from './editOwner';
import DownloadPDF from './modals/downloadPDF';
import { DUE_DILIGENCE_FUNNEL_LIST_QUERY, DUE_DILIGENCE_FUNNEL_QUERY, UPDATE_DUE_DILIGENCE_RECORD_MUTATION } from './queries';
import { TierItem } from './tierItem';

export const DueDiligenceFunnel = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  const [searchParams, setSearchParams] = useSearchParams();

  const activeFunnel = searchParams.get('activeFunnel') || '';

  const [funnel, setFunnel] = useState<DueDiligencePageListDueDiligenceFunnelsQuery['listDueDiligenceFunnels']>([]);
  const [sort, setSort] = useState<ISort>({ key: 'CLOSE_DATE', asc: false });
  const [mobileSort, setMobileSort] = useState(dueDiligenceFunnelSort[5].title);
  const sortItem = dueDiligenceFunnelSort.find((item) => item.title === mobileSort);
  const mobileSortList = dueDiligenceFunnelSort.map((item) => item.title);
  const [tiers, setTiers] = useState<DueDiligencePageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers']>([]);
  const [isTiersReady, setIsTiersReady] = useState(false);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'change-owner' });
  const [currentRow, setCurrentRow] = useState<
    DueDiligencePageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'][number]['records'][number] | null
  >(null);
  const [isTabMenuOpen, setIsTabMenuOpen] = useState(false);
  const [showTabs, setShowTabs] = useState(true);

  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const burgerMenuRef = useRef<HTMLDivElement | null>(null);

  useClickOutside<HTMLDivElement>(burgerMenuRef, () => setIsTabMenuOpen(false));

  const { loading: funnelListLoading } = useQuery(DUE_DILIGENCE_FUNNEL_LIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      data: {
        statusFilter: [DueDiligenceFunnelListStatusFilter.Published],
        sortDirection: DueDiligenceFunnelSortDirection.Asc,
        sortBy: DueDiligenceFunnelSortBy.Order
      }
    },
    onCompleted: (data) => {
      setFunnel(data.listDueDiligenceFunnels);
      if (!activeFunnel) {
        onChangeActiveFunnel(data.listDueDiligenceFunnels[0].id);
      }
    }
  });

  const {
    data: dueDiligenceFunnelData,
    loading: dueDiligenceFunnelLoading,
    refetch: dueDiligenceFunnelRefetch,
    networkStatus: dueDiligenceFunnelNetworkStatus
  } = useQuery(DUE_DILIGENCE_FUNNEL_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !activeFunnel,
    variables: {
      id: activeFunnel,
      order: DueDiligenceRecordsSortDirection.Desc,
      sort: DueDiligenceRecordsSortBy.CloseDate
    }
  });

  const { data: listDueDiligenceRecordsData, loading: listDueDiligenceRecordsLoading } = useQuery(LIST_DUE_DILIGENCE_RECORD_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !dueDiligenceFunnelData?.getDueDiligenceFunnel?.funnelStaticId,
    variables: {
      data: {
        sortBy: DueDiligenceRecordsListSortBy.InvestmentName,
        sortDirection: DueDiligenceRecordsSortDirection.Asc,
        statusFilter: [Security_Status.DueDiligence],
        funnelStaticIdFilter: [dueDiligenceFunnelData?.getDueDiligenceFunnel?.funnelStaticId]
      }
    }
  });

  useEffect(() => {
    if (dueDiligenceFunnelLoading || !dueDiligenceFunnelData || listDueDiligenceRecordsLoading || !listDueDiligenceRecordsData) return;

    const records = listDueDiligenceRecordsData?.listDueDiligenceRecords?.records || [];

    const filteredRecordsTiers = dueDiligenceFunnelData?.getDueDiligenceFunnel?.tiers?.map((tier) => {
      return {
        ...tier,
        records: tier.records.filter(
          (record) =>
            record.status !== DueDiligenceRecordStatus.Approved &&
            record.status !== DueDiligenceRecordStatus.Rejected &&
            record.investment.securityStatus === Security_Status.DueDiligence
        )
      };
    });

    const updateTiersWithRecords = () => {
      const filteredRecordsTiersCopy = [...filteredRecordsTiers];

      records.forEach((record: DealRegistryPageListDueDiligenceRecordsQuery['listDueDiligenceRecords']['records'][number]) => {
        const targetTier = filteredRecordsTiersCopy.find((tier) => tier.order === record.activeTier.order);

        if (targetTier && targetTier.id !== record.activeTier.id) {
          const recordAlreadyExists = targetTier.records.some((existingRecord) => existingRecord.id === record.id);

          if (!recordAlreadyExists) {
            const updatedTier = {
              ...targetTier,
              records: [...targetTier.records, record].sort((a, b) => {
                return new Date(b.investment.finalClose).getTime() - new Date(a.investment.finalClose).getTime();
              })
            };

            const tierIndex = filteredRecordsTiersCopy.findIndex((tier) => tier.order === targetTier.order);
            filteredRecordsTiersCopy[tierIndex] = updatedTier;
          }
        }
      });

      return filteredRecordsTiersCopy;
    };

    const tiersWithRecords = updateTiersWithRecords();
    setTiers(tiersWithRecords);
    setIsTiersReady(true);
  }, [dueDiligenceFunnelLoading, dueDiligenceFunnelData, listDueDiligenceRecordsLoading, listDueDiligenceRecordsData]);

  const [updateDueDiligenceRecord, { loading: updateDueDiligenceRecordLoading }] = useMutation(UPDATE_DUE_DILIGENCE_RECORD_MUTATION, {
    onCompleted: () => {
      dueDiligenceFunnelRefetch();
    }
  });

  useEffect(() => {
    if (isMobile) {
      setSort({
        key: sortItem?.key ?? 'CLOSE_DATE',
        asc: sortItem?.asc ?? false
      });
    }
  }, [activeFunnel, mobileSort]);

  useEffect(() => {
    const checkScreenWidth = () => {
      const tabsContainer = tabsContainerRef.current;

      if (tabsContainer) {
        const tabElements = Array.from(tabsContainer.children) as HTMLDivElement[];

        let totalTabWidth = 0;
        tabElements.forEach((tabElement) => {
          totalTabWidth += tabElement.offsetWidth;
        });

        const screenWidth = window.innerWidth;

        setShowTabs(totalTabWidth <= screenWidth - 200);
      }
    };

    if (tabsContainerRef.current) {
      checkScreenWidth();
    }

    window.addEventListener('resize', checkScreenWidth);

    return () => {
      window.removeEventListener('resize', checkScreenWidth);
    };
  }, [funnel, tabsContainerRef]);

  const handleTierClick = (row: DueDiligencePageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'][number]['records'][number]) => {
    navigate(`${row.id}?backToTitle=Due Diligence Funnel`);
  };

  const handleModifyDiligenceClick = (
    row: DueDiligencePageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'][number]['records'][number]
  ) => {
    navigate(`modify?recordId=${row.id}&funnelId=${activeFunnel}&status=${Security_Status.DueDiligence}&backToTitle=Due Diligence`);
  };

  const handleAddDiligenceClick = () => {
    navigate(`modify?status=${Security_Status.DueDiligence}&backToTitle=Due Diligence`);
  };

  const openModalWindow = (
    type: string,
    row: DueDiligencePageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'][number]['records'][number]
  ) => {
    setModalWindow({ ...modalWindow, isOpen: true, type });
    setCurrentRow(row);
  };

  const closeModalWindow = () => setModalWindow({ ...modalWindow, isOpen: false });

  useEffect(() => {
    if (!isTiersReady) return;

    const sortedData = [...tiers].map((tier) => ({
      ...tier,
      records: tier.records.sort((a, b) => {
        if (sort.key === 'INVESTMENT_NAME') {
          return sort.asc ? a.investment.name.localeCompare(b.investment.name) : b.investment.name.localeCompare(a.investment.name);
        } else if (sort.key === 'CLOSE_DATE' && (a.investment.finalClose || b.investment.finalClose)) {
          return sort.asc
            ? new Date(a.investment.finalClose).getTime() - new Date(b.investment.finalClose).getTime()
            : new Date(b.investment.finalClose).getTime() - new Date(a.investment.finalClose).getTime();
        } else if (sort.key === 'OWNER_NAME') {
          return sort.asc ? a.owner.firstName.localeCompare(b.owner.firstName) : b.owner.firstName.localeCompare(a.owner.firstName);
        } else if (sort.key === 'OPEN_TASKS') {
          return sort.asc ? a.activeTierOpenTasksCount - b.activeTierOpenTasksCount : b.activeTierOpenTasksCount - a.activeTierOpenTasksCount;
        }
        return 0;
      })
    }));

    setTiers(sortedData);
  }, [sort, isTiersReady]);

  const onChangeActiveFunnel = (funnelId: string, isMobile?: boolean) => {
    if (isMobile) {
      setIsTabMenuOpen(false);
    }
    setSearchParams(
      (prev) => {
        prev.set('activeFunnel', funnelId);
        return prev;
      },
      { replace: true }
    );
  };

  if (funnelListLoading) {
    return <LoaderOnWholeScreen size={60} />;
  }

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'change-owner' && (
        <EditOwner row={currentRow} isOpen={modalWindow.isOpen} onClose={closeModalWindow} updateOwner={updateDueDiligenceRecord} />
      )}
      {modalWindow.isOpen && modalWindow.type === 'download-pdf' && (
        <DownloadPDF isOpen={modalWindow.isOpen} recordId={currentRow?.id ?? null} onClose={closeModalWindow} />
      )}
      <MainWrap>
        <Header />
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          {showTabs ? (
            <Tabs ref={tabsContainerRef}>
              {funnel.map((item: DueDiligencePageListDueDiligenceFunnelsQuery['listDueDiligenceFunnels'][number]) => (
                <TabsTitle key={item.id} onClick={() => onChangeActiveFunnel(item.id)} activeTab={activeFunnel === item.id}>
                  {item.name}
                </TabsTitle>
              ))}
            </Tabs>
          ) : (
            <BurgerMenuWrapper ref={burgerMenuRef}>
              <ActiveTabWrapper>
                {isTabMenuOpen ? (
                  <CloseMenuIcon onClick={() => setIsTabMenuOpen(!isTabMenuOpen)} style={{ cursor: 'pointer' }} fill={themeVar('action.hover')} />
                ) : (
                  <BurgerMenuIcon onClick={() => setIsTabMenuOpen(!isTabMenuOpen)} style={{ cursor: 'pointer' }} fill={themeVar('action.hover')} />
                )}
                <ActiveTab activeTab>{funnel.find((item) => item.id === activeFunnel)?.name}</ActiveTab>
              </ActiveTabWrapper>
              {isTabMenuOpen && (
                <BurgerMenu>
                  {funnel.map((item: DueDiligencePageListDueDiligenceFunnelsQuery['listDueDiligenceFunnels'][number]) => (
                    <MenuItem key={item.id} onClick={() => onChangeActiveFunnel(item.id, true)} activeTab={activeFunnel === item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </BurgerMenu>
              )}
            </BurgerMenuWrapper>
          )}

          <TabsLine />
          <FilterBoxWrapper isMobile={isMobile}>
            <Button size="md" onClick={handleAddDiligenceClick} icon={<AddButtonIcon />}>
              Add Diligence
            </Button>
          </FilterBoxWrapper>
          {listDueDiligenceRecordsLoading ? (
            <Loader />
          ) : (
            <TiersWrapper>
              {tiers?.map((item) => (
                <TierItem
                  openModalWindow={openModalWindow}
                  handleTierClick={handleTierClick}
                  key={item.id}
                  sort={sort}
                  setSort={setSort}
                  handleModifyDiligenceClick={handleModifyDiligenceClick}
                  updateDueDiligenceRecordLoading={updateDueDiligenceRecordLoading}
                  currentRow={currentRow}
                  loading={
                    dueDiligenceFunnelNetworkStatus === NetworkStatus.setVariables || dueDiligenceFunnelNetworkStatus === NetworkStatus.loading
                  }
                  {...item}
                />
              ))}
            </TiersWrapper>
          )}
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const Tabs = styled.div`
  padding-top: 21px;
  display: flex;
  gap: 5px;
`;

const TabsLine = styled.div`
  border-bottom: 1px ${themeVar('border.base')} solid;
  margin-left: -50px;
  margin-right: -50px;
`;

const BurgerMenuWrapper = styled.div`
  margin-top: 15px;
  position: relative;
`;

const ActiveTabWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 15px;
`;

const ActiveTab = styled(TabsTitle)`
  width: min-content;
  margin: 0;
`;

const BurgerMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: absolute;
  top: 50px;
  z-index: 10;
  background: ${themeVar('layer.1')};
  width: 30%;
`;

const MenuItem = styled(TabsTitle)`
  margin: 0;
  border-radius: 0;
`;

const FilterBoxWrapper = styled.div<{ isMobile?: boolean }>`
  display: flex;
  justify-content: ${({ isMobile }) => (isMobile ? 'center' : 'flex-end')};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  align-items: center;
  gap: ${({ isMobile }) => (isMobile ? '10px' : '20px')};
  padding: 78px 0 10px 0;
`;

const TiersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 100px;
`;
